import React, { useState } from 'react';
import { useQuizQuestions } from '@/hooks/useQuizManagement';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Plus, HelpCircle } from 'lucide-react';
import { QuestionDialog } from './QuestionDialog';
import { QuestionList } from './QuestionList';
import { Quiz, QuizQuestion } from '@/types/quiz';

interface QuizBuilderProps {
  quiz: Quiz;
  onBack: () => void;
}

export const QuizBuilder = ({ quiz, onBack }: QuizBuilderProps) => {
  const { questions, loading, createQuestion, updateQuestion, deleteQuestion } = useQuizQuestions(quiz.id);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | null>(null);

  const handleCreateQuestion = async (
    questionData: Omit<QuizQuestion, 'id' | 'created_at' | 'updated_at'>, 
    options: { option_text: string; is_correct: boolean; explanation?: string; }[]
  ) => {
    try {
      await createQuestion({
        ...questionData,
        quiz_id: quiz.id,
        order_index: questions.length
      }, options);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to create question:', error);
    }
  };

  const handleEditQuestion = (question: QuizQuestion) => {
    setEditingQuestion(question);
    setIsDialogOpen(true);
  };

  const handleUpdateQuestion = async (
    questionData: Omit<QuizQuestion, 'id' | 'created_at' | 'updated_at'>, 
    options: { option_text: string; is_correct: boolean; explanation?: string; }[]
  ) => {
    if (!editingQuestion) return;
    
    try {
      // Don't pass quiz_id in updates since it should remain unchanged
      const { quiz_id, order_index, ...updateData } = questionData;
      await updateQuestion(editingQuestion.id, updateData, options);
      setIsDialogOpen(false);
      setEditingQuestion(null);
    } catch (error) {
      console.error('Failed to update question:', error);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await deleteQuestion(questionId);
    } catch (error) {
      console.error('Failed to delete question:', error);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingQuestion(null);
  };

  const totalPoints = questions.reduce((sum, q) => sum + q.points_value, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quizzes
          </Button>
          <div>
            <h3 className="text-lg font-semibold">{quiz.title}</h3>
            <p className="text-sm text-muted-foreground">
              {questions.length} questions • {totalPoints} total points
            </p>
          </div>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Question
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Quiz Questions
          </CardTitle>
          <CardDescription>
            Create and manage questions for this quiz. Questions will be presented in the order shown.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Loading questions...</div>
          ) : questions.length === 0 ? (
            <div className="text-center py-8">
              <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h4 className="text-lg font-semibold mb-2">No Questions</h4>
              <p className="text-muted-foreground mb-4">
                Add questions to make this quiz available to players.
              </p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Question
              </Button>
            </div>
          ) : (
            <QuestionList
              questions={questions}
              onEdit={handleEditQuestion}
              onDelete={handleDeleteQuestion}
            />
          )}
        </CardContent>
      </Card>

      <QuestionDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={editingQuestion ? handleUpdateQuestion : handleCreateQuestion}
        question={editingQuestion}
      />
    </div>
  );
};